import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { DataAccessFairsFacade, FairsConfiguration } from '@planone/bau365/data-access-fair-data';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';
import {
  EnrichedWatchlistFacade,
  EnrichedWatchlistItem,
  ItemCategory,
  WatchlistFacade,
  WatchlistItemIdentity,
} from '@planone/bau365/data-access-user';
import { BreadcrumbHelper } from '@planone/bau365/feature-breadcrumb';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { BreadcrumbElement, ModalBottomsheetHelper, ModalBottomsheetType } from '@planone/shared/ui-elements';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { BreadcrumbGenerator } from '@planone/shared/util-structured-data';
import { map, Observable, Subject, takeUntil } from 'rxjs';

import { ItemCategoryOption, itemTypes } from './page-watchlist.model';

@Component({
  selector: 'bau365-page-watchlist',
  templateUrl: './page-watchlist.component.html',
  styleUrls: ['./page-watchlist.component.scss'],
})
export class PageWatchlistComponent implements OnInit, OnDestroy {
  @ViewChild('filterDialogTitle')
  filterDialogTitle?: TemplateRef<never>;

  @ViewChild('filterDialogContent')
  filterDialogContent?: TemplateRef<never>;

  @ViewChild('filterDialogActions')
  filterDialogActions?: TemplateRef<never>;

  breadcrumbElements: BreadcrumbElement[] | undefined;
  ItemCategory = ItemCategory;
  itemTypes = itemTypes;

  itemsOnPage$: Observable<EnrichedWatchlistItem[]>;
  itemCount$: Observable<number>;
  pageCount$: Observable<number>;
  itemCategory$: Observable<ItemCategory>;
  currentPage$: Observable<number>;
  loaded$: Observable<boolean>;
  itemCategories$: Observable<ItemCategoryOption[]>;
  bauInsightsId$: Observable<string>;
  screen$: Observable<string | undefined | null>;
  fairsConfig$: Observable<FairsConfiguration | undefined | null>;

  emailSubject: string = localizedMeta['watchlistShareEmailSubject'];
  emailBody: string = localizedMeta['watchlistShareEmailBody'];

  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private readonly enrichedWatchlistFacade: EnrichedWatchlistFacade,
    private readonly watchlistFacade: WatchlistFacade,
    private readonly metaService: MetaService,
    private fairsFacade: DataAccessFairsFacade,
    private structuredDataGenerator: BreadcrumbGenerator,
    private breadcrumbHelper: BreadcrumbHelper,
    private bottomSheetHelper: ModalBottomsheetHelper,
    private renderer2: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    public togglesFacade: DataAccessTogglesFacade,
    public screensizeFacade: DataAccessScreensizeFacade
  ) {
    this.itemsOnPage$ = this.enrichedWatchlistFacade.itemsOnPage$;
    this.itemCount$ = this.enrichedWatchlistFacade.itemCount$;
    this.currentPage$ = this.enrichedWatchlistFacade.currentPage$;
    this.pageCount$ = this.enrichedWatchlistFacade.pageCount$;
    this.loaded$ = this.enrichedWatchlistFacade.loaded$;
    this.itemCategory$ = this.enrichedWatchlistFacade.itemCategory$;
    this.itemCategories$ = this.enrichedWatchlistFacade.allItemCategories$;
    this.bauInsightsId$ = this.fairsFacade.fairsConfig$.pipe(map((fairsConfig) => fairsConfig.bauInsights));
    this.screen$ = this.screensizeFacade.size$;
    this.fairsConfig$ = this.fairsFacade.fairsConfig$;

    this.metaService.setTitle(localizedMeta['titleWatchlist']);
  }

  ngOnInit(): void {
    this.fairsFacade.fairConfig$.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      const breadcrumbData = this.breadcrumbHelper.prepareForWatchlist();

      this.breadcrumbElements = breadcrumbData.elements;

      this.structuredDataGenerator.validateBreadcrumbStructuredData(
        this.renderer2,
        breadcrumbData.structuredData,
        this.document.head
      );
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onFilterButtonClicked(screenSize: string) {
    this.bottomSheetHelper.openDialog(
      ModalBottomsheetType.general,
      this.filterDialogTitle,
      this.filterDialogContent,
      screenSize,
      {}, // we don't add any data here that isn't collected by the helper anyway
      {
        height: '75vh',
      },
      {
        width: '550px',
        height: '660px',
      },
      this.filterDialogActions
    );
  }

  onItemCategoryChanged(itemCategory: ItemCategory) {
    this.enrichedWatchlistFacade.setItemCategory(itemCategory);
  }

  onSubmitItemCategory() {
    this.bottomSheetHelper.closeDialog();
  }

  onResetItemCategory() {
    this.enrichedWatchlistFacade.setItemCategory(ItemCategory.all);
    this.bottomSheetHelper.closeDialog();
  }

  onChangeWatchlistPage(page: number) {
    this.enrichedWatchlistFacade.openWatchlistPage(++page);
  }

  onDeleteItem(item: WatchlistItemIdentity) {
    this.watchlistFacade.deleteWatchlistItem(item, 'watchlist');
  }

  onShareItem(item: WatchlistItemIdentity) {
    this.watchlistFacade.shareWatchlistItem(item, 'watchlist');
  }

  onShareItemAction({ network, item }: { network: string; item: WatchlistItemIdentity }) {
    if (item) this.watchlistFacade.shareWatchlistItemAction(network, item, 'watchlist');
  }
}
